import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CategoryMenu from '../components/CategoryMenu';
import SEO, { CollectionPageSchema } from '../components/SEO';
import { Helmet } from 'react-helmet-async';
import { Search, ShoppingCart, Heart, Filter, X, Zap, Grid3X3, List, Eye } from 'lucide-react';
import { AppContext } from '../App';
import { toast } from 'sonner';
import { trackViewItemList, trackSearch } from '../utils/analytics';
import { productUrl } from '../utils/slug';
import { Slider } from '../components/ui/slider';
import { productImageProps } from '../utils/image';
import QuickViewModal from '../components/QuickViewModal';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const translations = {
  en: {
    registeredMail: 'Registered Mail: $4',
    freeShipping: 'Free Shipping $100+',
    discount: '10% Discount $100+'
  },
  tr: {
    registeredMail: 'Taahhütlü Posta: $4',
    freeShipping: 'Ücretsiz Kargo $100+',
    discount: '%10 İndirim $100+'
  },
  el: {
    registeredMail: 'Συστημένη Αποστολή: $4',
    freeShipping: 'Δωρεάν Αποστολή $100+',
    discount: 'Έκπτωση 10% $100+'
  },
  de: {
    registeredMail: 'Einschreiben: $4',
    freeShipping: 'Kostenloser Versand $100+',
    discount: '10% Rabatt $100+'
  }
};

const ShopPage = () => {
  const navigate = useNavigate();
  const { fetchCart, wishlistIds, toggleWishlist, language, convertPrice, currencySymbols, displayCurrency, openCartDrawer } = useContext(AppContext);
  const t = translations[language] || translations.en;
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]); // Store all products for pagination
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  
  // Items per page state - retrieve from localStorage or default to 24
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('shopItemsPerPage');
    return saved ? saved : '24';
  });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);

  // View mode (grid or list)
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('shopViewMode') || 'grid');
  
  // Load saved filters from localStorage
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem('shopFilters');
    const defaults = {
      category: searchParams.get('category') || '',
      country: '',
      condition: '',
      minPrice: '',
      maxPrice: '',
      year: '',
      minYear: '',
      maxYear: '',
      issue: '',
      search: ''
    };
    return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
  });

  // Load saved sort preferences
  const [sortBy, setSortBy] = useState(() => {
    const saved = localStorage.getItem('shopSortBy');
    return saved || 'title';
  });
  
  const [sortDir, setSortDir] = useState(() => {
    const saved = localStorage.getItem('shopSortDir');
    return saved || 'asc';
  });

  const conditions = ['Mint', 'Used', 'CTO'];
  const itemsPerPageOptions = ['24', '48', '72', 'all'];
  const years = Array.from({length: 2025 - 1974 + 1}, (_, i) => 2025 - i); // 2025 down to 1974

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    // Update filters when URL params change
    const categoryParam = searchParams.get('category');
    const searchParam = searchParams.get('search');
    const updates = {};
    if (categoryParam && categoryParam !== filters.category) updates.category = categoryParam;
    if (searchParam && searchParam !== filters.search) updates.search = searchParam;
    if (Object.keys(updates).length > 0) setFilters(prev => ({ ...prev, ...updates }));
  }, [searchParams]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters change
    fetchProducts();
    // Save filters to localStorage
    localStorage.setItem('shopFilters', JSON.stringify(filters));
  }, [filters, categories, sortBy, sortDir]);
  
  // Save sort preferences
  useEffect(() => {
    localStorage.setItem('shopSortBy', sortBy);
    localStorage.setItem('shopSortDir', sortDir);
  }, [sortBy, sortDir]);
  
  useEffect(() => {
    // Re-apply pagination when items per page or page changes
    if (itemsPerPage === 'all') {
      setProducts(allProducts);
      setCurrentPage(1);
    } else {
      const limit = parseInt(itemsPerPage);
      const startIndex = (currentPage - 1) * limit;
      const endIndex = startIndex + limit;
      setProducts(allProducts.slice(startIndex, endIndex));
    }
  }, [itemsPerPage, currentPage, allProducts]);

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value);
    setCurrentPage(1); // Reset to first page when changing items per page
    localStorage.setItem('shopItemsPerPage', value);
  };

  // Calculate total pages
  const getTotalPages = () => {
    if (itemsPerPage === 'all') return 1;
    return Math.ceil(allProducts.length / parseInt(itemsPerPage));
  };

  const handleNextPage = () => {
    const totalPages = getTotalPages();
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('active_only', 'true');
      if (filters.category) params.append('category', filters.category);
      if (filters.country) params.append('country', filters.country);
      if (filters.condition) params.append('condition', filters.condition);
      if (filters.minPrice) params.append('min_price', filters.minPrice);
      if (filters.maxPrice) params.append('max_price', filters.maxPrice);
      if (filters.minYear) params.append('min_year', filters.minYear);
      if (filters.maxYear) params.append('max_year', filters.maxYear);
      if (filters.year) params.append('year', filters.year);
      if (filters.issue) params.append('issue', filters.issue);
      if (filters.search) params.append('search', filters.search);
      params.append('limit', '1000');
      params.append('sort', sortBy);
      params.append('dir', sortDir);

      const response = await axios.get(`${API}/products?${params.toString()}`);
      
      // Handle new paginated response format
      const productsData = response.data.products || response.data;
      const activeProducts = Array.isArray(productsData) 
        ? productsData.filter(p => !p.deleted && !p.archived && p.stock > 0)
        : [];
      
      // Apply hierarchical category filtering on frontend if category is selected
      let filteredProducts = activeProducts;
      if (filters.category) {
        const selectedCat = categories.find(c => c.slug === filters.category);
        if (selectedCat) {
          setSelectedCategoryName(selectedCat.name);
          
          // Get all descendant category slugs (including the selected one)
          const getAllDescendantSlugs = (catSlug) => {
            const slugs = [catSlug];
            const children = categories.filter(c => c.parent_id === categories.find(cat => cat.slug === catSlug)?.id);
            children.forEach(child => {
              slugs.push(...getAllDescendantSlugs(child.slug));
            });
            return slugs;
          };
          
          const relevantSlugs = getAllDescendantSlugs(filters.category);
          
          // Filter products that have any of the relevant category slugs
          filteredProducts = activeProducts.filter(product => 
            product.categories && product.categories.some(catSlug => relevantSlugs.includes(catSlug))
          );
        }
      } else {
        setSelectedCategoryName('');
      }
      
      setAllProducts(filteredProducts);
      if (filters.search) trackSearch(filters.search);
      trackViewItemList(filters.category || 'All Products', filteredProducts);

      // Apply items per page
      if (itemsPerPage === 'all') {
        setProducts(filteredProducts);
      } else {
        const limit = parseInt(itemsPerPage);
        setProducts(filteredProducts.slice(0, limit));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleAddToCart = async (productId) => {
    try {
      await axios.post(`${API}/cart/add?product_id=${productId}&quantity=1`, {}, { withCredentials: true });
      await fetchCart();
      openCartDrawer();
    } catch (error) {
      console.error('Error adding to cart:', error);
      toast.error('Failed to add to cart');
    }
  };

  const handleQuickBuy = async (productId) => {
    try {
      await axios.post(`${API}/cart/add?product_id=${productId}&quantity=1`, {}, { withCredentials: true });
      await fetchCart();
      navigate('/cart');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  return (
    <div className="shop-page">
      <SEO
        title={selectedCategoryName ? `${selectedCategoryName} - TRNC Stamps & Collections` : "Shop Stamps | Rare TRNC Stamps & Collections"}
        description={selectedCategoryName
          ? `Browse ${allProducts.length} ${selectedCategoryName} items. Rare philatelic collectibles from North Cyprus Philately. Worldwide shipping.`
          : "Browse our extensive collection of North Cyprus stamps, year sets, first day covers, and rare philatelic items. Worldwide shipping available."}
        keywords="buy stamps, TRNC stamps shop, North Cyprus philately, stamp collections, year sets for sale"
        url={`https://www.northcyprusphilately.com/shop${filters.category ? `?category=${filters.category}` : ''}`}
        lang={language}
      />
      {itemsPerPage !== 'all' && getTotalPages() > 1 && (
        <Helmet>
          {currentPage > 1 && <link rel="prev" href={`https://www.northcyprusphilately.com/shop?${filters.category ? `category=${filters.category}&` : ''}page=${currentPage - 1}`} />}
          {currentPage < getTotalPages() && <link rel="next" href={`https://www.northcyprusphilately.com/shop?${filters.category ? `category=${filters.category}&` : ''}page=${currentPage + 1}`} />}
        </Helmet>
      )}
      <CollectionPageSchema
        products={products}
        categoryName={selectedCategoryName}
        currentPage={currentPage}
        totalItems={allProducts.length}
        pageUrl={`https://www.northcyprusphilately.com/shop${filters.category ? `?category=${filters.category}` : ''}`}
      />
      <Header />
      
      {/* Shipping & Discount Banner */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-3">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center items-center gap-4 md:gap-8 text-sm">
            <span>🚢 {t.registeredMail}</span>
            <span className="hidden md:inline">|</span>
            <span>✓ {t.freeShipping}</span>
            <span className="hidden md:inline">|</span>
            <span>🎁 {t.discount}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Mobile Filter Button */}
        <button
          onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
          className="md:hidden mb-4 flex items-center gap-2 btn-primary w-full"
        >
          <Filter size={20} />
          {isMobileFilterOpen ? 'Hide Filters' : 'Show Filters'}
        </button>

        <div className="flex gap-8 relative">
          {/* Mobile Overlay */}
          {isMobileFilterOpen && (
            <div 
              className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setIsMobileFilterOpen(false)}
            />
          )}

          {/* Sidebar Filters */}
          <div className={`filter-sidebar space-y-6 ${
            isMobileFilterOpen 
              ? 'fixed top-0 left-0 h-full w-80 bg-white z-50 p-6 overflow-y-auto shadow-2xl' 
              : 'hidden md:block md:w-64 md:h-fit md:static'
          }`} data-testid="filter-sidebar">
            {/* Mobile Close Button */}
            {isMobileFilterOpen && (
              <button
                onClick={() => setIsMobileFilterOpen(false)}
                className="md:hidden absolute top-4 right-4 text-gray-600 hover:text-gray-900"
              >
                <X size={24} />
              </button>
            )}

            <h3 className="text-xl font-bold mb-4 md:hidden">Filters & Search</h3>

            {/* Search Bar */}
            <div className="mb-6">
              <label className="block font-semibold mb-2">Search</label>
              <input 
                type="text" 
                placeholder="Catalogue #, year, theme..." 
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full"
              />
              <p className="text-xs text-gray-500 mt-1">Search by title, description, or catalogue number</p>
            </div>

            {/* Hover Category Menu */}
            <CategoryMenu 
              onCategorySelect={(category) => {
                handleFilterChange('category', category);
                setIsMobileFilterOpen(false);
              }}
              selectedCategory={filters.category}
            />
            
            <div className="mb-6">
              <label className="block font-semibold mb-2">
                Year Range
                {(filters.minYear || filters.maxYear) && (
                  <span className="ml-2 text-[#C8A951] font-normal text-sm">
                    {filters.minYear || 1974} – {filters.maxYear || 2025}
                  </span>
                )}
              </label>
              <Slider
                min={1974}
                max={2025}
                step={1}
                value={[parseInt(filters.minYear) || 1974, parseInt(filters.maxYear) || 2025]}
                onValueChange={([min, max]) => {
                  setFilters(prev => ({
                    ...prev,
                    minYear: min === 1974 ? '' : String(min),
                    maxYear: max === 2025 ? '' : String(max),
                    year: '',
                  }));
                }}
                className="mt-3 [&_.bg-primary]:bg-[#C8A951] [&_.bg-primary\/20]:bg-gray-200"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>1974</span><span>2025</span>
              </div>
            </div>

            <div className="mb-6">
              <label className="block font-semibold mb-2">Issue / Catalogue #</label>
              <input
                type="text"
                placeholder="e.g., 265FFIGZ52030"
                value={filters.issue}
                onChange={(e) => handleFilterChange('issue', e.target.value)}
                className="w-full"
              />
            </div>

            <div className="mb-6">
              <label className="block font-semibold mb-2">Condition</label>
              <select
                value={filters.condition}
                onChange={(e) => handleFilterChange('condition', e.target.value)}
                className="w-full"
                data-testid="filter-condition"
              >
                <option value="">All Conditions</option>
                {conditions.map(cond => (
                  <option key={cond} value={cond}>{cond}</option>
                ))}
              </select>
            </div>

            <div className="mb-6">
              <label className="block font-semibold mb-2">
                Price Range ($)
                {(filters.minPrice || filters.maxPrice) && (
                  <span className="ml-2 text-[#C8A951] font-normal text-sm">
                    ${filters.minPrice || 0} – ${filters.maxPrice || 500}
                  </span>
                )}
              </label>
              <Slider
                min={0}
                max={500}
                step={5}
                value={[parseInt(filters.minPrice) || 0, parseInt(filters.maxPrice) || 500]}
                onValueChange={([min, max]) => {
                  setFilters(prev => ({
                    ...prev,
                    minPrice: min === 0 ? '' : String(min),
                    maxPrice: max === 500 ? '' : String(max),
                  }));
                }}
                className="mt-3 [&_.bg-primary]:bg-[#C8A951] [&_.bg-primary\/20]:bg-gray-200"
                data-testid="filter-price-slider"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>$0</span><span>$500+</span>
              </div>
            </div>

            <button
              onClick={() => {
                setFilters({ category: '', country: '', condition: '', minPrice: '', maxPrice: '', year: '', minYear: '', maxYear: '', issue: '', search: '' });
                setIsMobileFilterOpen(false);
                localStorage.removeItem('shopFilters');
              }}
              className="w-full btn-secondary"
              data-testid="clear-filters-btn"
            >
              Clear All Filters
            </button>

            {/* Mobile Apply Button */}
            {isMobileFilterOpen && (
              <button
                onClick={() => setIsMobileFilterOpen(false)}
                className="w-full btn-primary md:hidden mt-4"
              >
                Apply Filters
              </button>
            )}

            {/* Recently Viewed (sidebar) */}
            {(() => {
              try {
                const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]').slice(0, 4);
                if (viewed.length === 0) return null;
                return (
                  <div className="mt-6 pt-6 border-t hidden md:block">
                    <h4 className="font-semibold mb-3 text-sm">{language === 'tr' ? 'Son Görüntülenen' : 'Recently Viewed'}</h4>
                    <div className="space-y-3">
                      {viewed.map(p => (
                        <Link key={p.id} to={productUrl(p)} className="flex items-center gap-2 hover:bg-gray-50 rounded p-1 transition">
                          {p.images?.[0] && (
                            <img src={p.images[0].startsWith('http') ? p.images[0] : `${BACKEND_URL}${p.images[0]}`} alt="" className="w-10 h-10 object-cover rounded" loading="lazy" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium truncate">{p[`title_${language}`] || p.title}</p>
                            <p className="text-xs font-bold" style={{color: '#B8941E'}}>${p.discounted_price && p.discounted_price < p.price ? p.discounted_price : p.price}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              } catch { return null; }
            })()}
          </div>
          
          {/* Products Grid */}
          <div className="flex-1">
            {/* Active Filter Tags */}
            {(() => {
              const activeFilters = [];
              const emptyFilters = { category: '', country: '', condition: '', minPrice: '', maxPrice: '', year: '', minYear: '', maxYear: '', issue: '', search: '' };
              if (filters.category) activeFilters.push({ key: 'category', label: selectedCategoryName || filters.category });
              if (filters.condition) activeFilters.push({ key: 'condition', label: filters.condition });
              if (filters.year) activeFilters.push({ key: 'year', label: filters.year });
              if (filters.minYear || filters.maxYear) activeFilters.push({ key: 'yearRange', label: `${filters.minYear || 1974}–${filters.maxYear || 2025}` });
              if (filters.search) activeFilters.push({ key: 'search', label: `"${filters.search}"` });
              if (filters.minPrice || filters.maxPrice) activeFilters.push({ key: 'price', label: `$${filters.minPrice || '0'} – $${filters.maxPrice || '500+'}` });
              if (filters.issue) activeFilters.push({ key: 'issue', label: filters.issue });
              if (activeFilters.length === 0) return null;
              return (
                <div className="mb-4 flex flex-wrap gap-2 items-center">
                  <span className="text-xs text-gray-500 font-semibold">{language === 'tr' ? 'Filtreler:' : 'Filters:'}</span>
                  {activeFilters.map(f => (
                    <span key={f.key} className="inline-flex items-center gap-1 bg-[#C8A951]/10 text-[#B8941E] text-xs font-semibold px-3 py-1 rounded-full border border-[#C8A951]/30">
                      {f.label}
                      <button onClick={() => {
                        if (f.key === 'price') setFilters(prev => ({ ...prev, minPrice: '', maxPrice: '' }));
                        else if (f.key === 'yearRange') setFilters(prev => ({ ...prev, minYear: '', maxYear: '' }));
                        else setFilters(prev => ({ ...prev, [f.key]: '' }));
                      }} className="ml-1 hover:text-red-500 transition">&times;</button>
                    </span>
                  ))}
                  <button onClick={() => { setFilters(emptyFilters); localStorage.removeItem('shopFilters'); }} className="text-xs text-red-500 hover:text-red-700 font-semibold underline">
                    {language === 'tr' ? 'Tümünü Temizle' : 'Clear All'}
                  </button>
                </div>
              );
            })()}

            {/* Header with Items Per Page */}
            <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold" data-testid="shop-title">
                  {selectedCategoryName || 'Shop'}
                </h2>
                <p className="text-sm md:text-base text-gray-600">
                  {itemsPerPage === 'all' 
                    ? `${allProducts.length} items` 
                    : `Showing ${Math.min((currentPage - 1) * parseInt(itemsPerPage) + 1, allProducts.length)} - ${Math.min(currentPage * parseInt(itemsPerPage), allProducts.length)} of ${allProducts.length} items (Page ${currentPage} of ${getTotalPages()})`
                  }
                </p>
              </div>
              
              {/* Sorting and Items Per Page Controls */}
              <div className="flex items-center gap-3 flex-wrap">
                {/* Sort By Dropdown */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-semibold text-gray-700">Sort:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C8A951] transition text-sm"
                  >
                    <option value="title">Name</option>
                    <option value="price">Price</option>
                    <option value="stock">Stock</option>
                    <option value="category">Category</option>
                  </select>
                  
                  {/* Sort Direction Toggle */}
                  <button
                    onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
                    className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition text-sm"
                    title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                    aria-label={`Sort ${sortDir === 'asc' ? 'descending' : 'ascending'}`}
                  >
                    {sortDir === 'asc' ? '↑' : '↓'}
                  </button>
                </div>

                <div className="h-6 w-px bg-gray-300 hidden md:block"></div>

                {/* Grid/List Toggle */}
                <div className="hidden md:flex items-center border border-gray-300 rounded-lg overflow-hidden">
                  <button
                    onClick={() => { setViewMode('grid'); localStorage.setItem('shopViewMode', 'grid'); }}
                    className={`p-2 transition ${viewMode === 'grid' ? 'bg-[#C8A951] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                    aria-label="Grid view"
                  >
                    <Grid3X3 size={16} />
                  </button>
                  <button
                    onClick={() => { setViewMode('list'); localStorage.setItem('shopViewMode', 'list'); }}
                    className={`p-2 transition ${viewMode === 'list' ? 'bg-[#C8A951] text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                    aria-label="List view"
                  >
                    <List size={16} />
                  </button>
                </div>

                <div className="h-6 w-px bg-gray-300 hidden md:block"></div>

                <label className="text-sm font-semibold text-gray-700 hidden md:block">Per Page:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  className="px-3 md:px-4 py-2 border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C8A951] transition text-sm"
                >
                  <option value="24">24</option>
                  <option value="48">48</option>
                  <option value="72">72</option>
                  <option value="all">See All</option>
                </select>
                
                {/* Navigation Buttons */}
                {itemsPerPage !== 'all' && getTotalPages() > 1 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePrevPage}
                      disabled={currentPage === 1}
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      title="Previous Page"
                    >
                      ‹
                    </button>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage >= getTotalPages()}
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                      title="Next Page"
                    >
                      ›
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="product-card animate-pulse">
                    <div className="bg-gray-200 h-48 rounded-t-lg"></div>
                    <div className="p-4 space-y-3">
                      <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                      <div className="flex justify-between">
                        <div className="h-4 bg-gray-200 rounded w-16"></div>
                        <div className="h-4 bg-gray-200 rounded w-12"></div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="h-7 bg-gray-200 rounded w-20"></div>
                        <div className="h-9 bg-gray-200 rounded w-9"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16" data-testid="no-products">
                <p className="text-xl text-gray-600">No stamps found. Try adjusting your filters.</p>
              </div>
            ) : (
              <div className={viewMode === 'list' ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'}>
                {products.map((product) => (
                  <div
                    key={product.id}
                    className={viewMode === 'list' ? 'product-card flex flex-row gap-4' : 'product-card'}
                    data-testid={`product-card-${product.id}`}
                  >
                    <div className={`relative ${viewMode === 'list' ? 'w-32 md:w-48 flex-shrink-0' : ''}`}>
                    <Link to={productUrl(product)}>
                      <img
                        {...productImageProps(
                          product.images?.[0] || '',
                          `${product.title} - ${product.condition || 'collectible'} TRNC stamp`,
                          { size: viewMode === 'list' ? 'thumb' : 'card' }
                        )}
                        width="400"
                        height="300"
                        className={`img-loading ${viewMode === 'list' ? 'w-full h-full object-cover rounded-l-lg cursor-pointer' : 'cursor-pointer'}`}
                        onLoad={(e) => { e.target.classList.remove('img-loading'); e.target.classList.add('img-loaded'); }}
                      />
                    </Link>
                    {product.discount_rate > 0 && (
                      <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                        -{product.discount_rate}%
                      </span>
                    )}
                    {product.stock > 0 && product.stock <= 3 && (
                      <span className="absolute top-2 right-2 bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded animate-pulse">
                        Only {product.stock} left!
                      </span>
                    )}
                    {product.created_at && (Date.now() - new Date(product.created_at).getTime()) < 7 * 24 * 60 * 60 * 1000 && (
                      <span className="absolute bottom-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">
                        NEW
                      </span>
                    )}
                    {product.price >= 100 && (
                      <span className="absolute bottom-2 right-2 bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                        FREE SHIP
                      </span>
                    )}
                    </div>
                    <div className={viewMode === 'list' ? 'flex-1 p-4 flex flex-col justify-between' : 'p-4'}>
                      <div>
                        <h3 className="text-lg font-semibold mb-2">
                          <Link to={productUrl(product)} className="hover:text-[#C8A951] transition">
                            {product[`title_${language}`] || product.title}
                          </Link>
                        </h3>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-600">{product.condition}</span>
                          <span className="text-sm text-gray-600">{product.year}</span>
                        </div>
                        {viewMode === 'list' && product[`description_${language}`] && (
                          <p className="text-sm text-gray-500 mb-3 line-clamp-2 hidden md:block">{product[`description_${language}`] || product.description}</p>
                        )}
                      </div>
                      <div className="flex justify-between items-center">
                        <div>
                          {product.discounted_price && product.discounted_price < product.price ? (
                            <>
                              <span className="text-2xl font-bold" style={{color: '#B8941E'}}>{currencySymbols[displayCurrency]}{convertPrice(product.discounted_price)}</span>
                              <span className="text-sm text-gray-400 line-through ml-1">{currencySymbols[displayCurrency]}{convertPrice(product.price)}</span>
                            </>
                          ) : (
                            <span className="text-2xl font-bold" style={{color: '#B8941E'}}>{currencySymbols[displayCurrency]}{convertPrice(product.price)}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => { e.stopPropagation(); setQuickViewProduct(product); }}
                            className="p-2 rounded hover:bg-blue-50 transition"
                            aria-label="Quick view"
                            title={language === 'tr' ? 'Hızlı Görüntüle' : 'Quick View'}
                          >
                            <Eye size={18} className="text-gray-400 hover:text-blue-500" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); toggleWishlist(product.id); }}
                            className="p-2 rounded hover:bg-red-50 transition"
                            aria-label="Toggle wishlist"
                          >
                            <Heart size={18} className={wishlistIds.includes(product.id) ? 'fill-red-500 text-red-500' : 'text-gray-400 hover:text-red-400'} />
                          </button>
                          <button
                            className="btn-secondary py-1.5 px-2"
                            onClick={() => handleAddToCart(product.id)}
                            data-testid={`add-to-cart-${product.id}`}
                            title={language === 'tr' ? 'Sepete Ekle' : 'Add to Cart'}
                          >
                            <ShoppingCart className="inline" size={18} />
                          </button>
                          <button
                            className="btn-primary py-1.5 px-2 text-xs flex items-center gap-1"
                            onClick={() => handleQuickBuy(product.id)}
                            title={language === 'tr' ? 'Hemen Al' : 'Buy Now'}
                          >
                            <Zap size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Bottom Pagination Controls */}
            {!loading && products.length > 0 && itemsPerPage !== 'all' && getTotalPages() > 1 && (
              <div className="mt-8 flex justify-end items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <label className="text-sm font-semibold text-gray-700">Items Per Page:</label>
                <select
                  value={itemsPerPage}
                  onChange={(e) => handleItemsPerPageChange(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#C8A951] transition"
                >
                  <option value="24">24</option>
                  <option value="48">48</option>
                  <option value="72">72</option>
                  <option value="all">See All</option>
                </select>
                
                {/* Navigation Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    title="Previous Page"
                  >
                    ‹
                  </button>
                  <span className="text-sm text-gray-700 px-2">
                    Page {currentPage} of {getTotalPages()}
                  </span>
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage >= getTotalPages()}
                    className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    title="Next Page"
                  >
                    ›
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {quickViewProduct && (
        <QuickViewModal product={quickViewProduct} onClose={() => setQuickViewProduct(null)} />
      )}
      <Footer />
    </div>
  );
};

export default ShopPage;